<script setup>
/**
 * A LINHA "Mídia, links e docs" no card do contato.
 *
 * Só isso: ícone, título, contador e seta. O conteúdo abre NO LUGAR do card
 * (ConversationAttachmentsPanel), e não num modal centralizado — pedido do
 * Victor (2026-08-07), olhando o Digisac: "quando eu clicar em mídias, links e
 * docs, quero que abra na posição onde está o card, não um modal no meio".
 *
 * Ele tem razão: um modal por cima da tela tira a conversa de vista para uma
 * consulta que é sobre a conversa. No Digisac o painel troca de conteúdo e
 * volta com uma seta — a conversa nunca sai da frente.
 *
 * Quem busca os dados é o ContactPanel (o pai), porque a mesma informação
 * alimenta esta linha e o painel. Duas buscas dariam dois contadores.
 */
import { useI18n } from 'vue-i18n';

defineProps({
  total: { type: Number, default: 0 },
});

const emit = defineEmits(['abrir']);

const { t } = useI18n();
</script>

<template>
  <button type="button" class="anexos" @click="emit('abrir')">
    <span class="anexos__ico" aria-hidden="true">
      <i class="i-lucide-folder-open" />
    </span>
    <span class="anexos__titulo">
      {{ t('CONVERSATION.ATTACHMENTS.TITLE') }}
    </span>
    <span class="anexos__total">{{ total || 0 }}</span>
    <i class="i-lucide-chevron-right anexos__seta" aria-hidden="true" />
  </button>
</template>

<style scoped>
/* A linha vive no topo AZUL-MARINHO da ficha, junto de "Marcar como não
   lida" — daí o vidro claro e as letras brancas, os mesmos da ação irmã. */
.anexos {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  width: 100%;
  margin-top: var(--esp-3);
  padding: var(--esp-2) var(--esp-3);
  border: 1px solid rgb(255 255 255 / 12%);
  border-radius: var(--r-4);
  background: rgb(255 255 255 / 6%);
  text-align: left;
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    border-color var(--mov-1) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva);
}

.anexos:hover {
  border-color: rgb(255 255 255 / 26%);
  background: rgb(255 255 255 / 12%);
}

.anexos:active {
  scale: 0.98;
}

.anexos:focus-visible {
  outline: 2px solid rgb(255 255 255 / 70%);
  outline-offset: 2px;
}

.anexos__ico {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: var(--r-2);
  background: rgb(255 255 255 / 12%);
  color: rgb(255 255 255);
}

.anexos__ico > i {
  display: block;
  font-size: var(--ico-2);
  line-height: 1;
}

.anexos__titulo {
  flex: 1;
  min-width: 0;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  color: rgb(255 255 255 / 92%);
}

.anexos__total {
  display: inline-grid;
  place-items: center;
  min-width: 22px;
  height: 20px;
  padding: 0 var(--esp-2);
  border-radius: var(--r-redondo);
  background: rgb(255 255 255 / 14%);
  font-size: var(--txt-selo);
  font-weight: var(--peso-forte);
  color: rgb(255 255 255);
  font-variant-numeric: tabular-nums;
}

/* A seta anda um passo no cursor: diz que a linha LEVA a algum lugar. */
.anexos__seta {
  flex-shrink: 0;
  font-size: var(--ico-2);
  color: rgb(255 255 255 / 70%);
  transition: translate var(--mov-2) var(--mov-curva-pulo);
}

.anexos:hover .anexos__seta {
  translate: 3px 0;
  color: rgb(255 255 255);
}

@media (prefers-reduced-motion: reduce) {
  .anexos,
  .anexos__seta {
    transition: none;
  }
}
</style>
